import {
  StateDefinition,
  StateKeyValues,
  TypeOfStateDeclaration,
} from '@iwentsourcing/core/storage/state-declaration.js';
import { BrowseResult } from '@iwentsourcing/core/storage/browse-result.js';
import { createRemoteObservedIndexClient } from './create-remote-observed-index-client.js';
import { ObserveRangeOptions } from '@iwentsourcing/core/storage/observer/observe-range-options.js';
import { IterateOptions } from '@iwentsourcing/core/storage/observer/iterate-options.js';
import { RemoteDescription } from './remote-description.js';
import { RpcClient } from '../api/create-rpc-client.js';
import { SessionManager } from '../auth/jwt-session.js';
import { Lazy } from '@iwentsourcing/core/lazy/lazy.js';
import { mapToState } from '@iwentsourcing/core/runtime/get-state-data-keys.js';
import { ObservedIndexClient } from '@iwentsourcing/core/storage/observer/observed-index-client.js';
import { ObservedClient } from '@iwentsourcing/core/storage/observer/observed-client.js';
import { DataSet } from '@iwentsourcing/core/storage/data-set.js';
import { Telemetry } from '@iwentsourcing/core/tracer/telemetry.js';

export function createRemoteObservedClient<
  TState extends StateDefinition<any, any, any>,
>(
  state: TState,
  rpcClient: RpcClient,
  session: SessionManager,
  lazyVersion: Lazy<string>,
  type: 'stream' | 'aggregation',
  streamIdentifier: RemoteDescription,
  stateName: string,
  telemetry: Telemetry,
): ObservedClient<TState> {
  return {
    async *observe(
      id: StateKeyValues<TState>,
      options,
    ): AsyncGenerator<DataSet<TypeOfStateDeclaration<TState>> | null> {
      const iterator = rpcClient['state.get.observe'].call(
        {
          type,
          tenant: streamIdentifier.tenant,
          realm: streamIdentifier.realm,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          state: stateName,
          id,
          args: streamIdentifier.args,
          token: await session.resolveToken(),
        },
        options.signal,
      );
      for await (const item of iterator) {
        if (!item) {
          yield null;
          continue;
        }

        yield {
          data: mapToState(state, item.data, telemetry),
          keys: item.keys,
        };
      }
    },
    async *iterate(
      bookmark: StateKeyValues<TState> | null,
      iterateOptions: IterateOptions,
    ): AsyncGenerator<DataSet<any>> {
      const iterator = rpcClient['state.iterate'].call(
        {
          tenant: streamIdentifier.tenant,
          realm: streamIdentifier.realm,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          state: stateName,
          bookmark: bookmark ?? undefined,
          direction: iterateOptions.dir,
          open: iterateOptions.open,
          token: await session.resolveToken(),
          args: streamIdentifier.args,
          type,
          take: undefined,
        },
        iterateOptions.signal,
      );

      for await (const item of iterator) {
        yield {
          data: mapToState(state, item.data, telemetry),
          keys: item.keys,
        };
      }
    },
    index<K extends keyof TState['indices'] & string>(
      index: K,
    ): ObservedIndexClient<TState, TState['indices'][K]> {
      return createRemoteObservedIndexClient(
        state,
        state.indices[index],
        0,
        rpcClient,
        session,
        lazyVersion,
        type,
        streamIdentifier,
        stateName,
        index,
        {},
        telemetry,
      );
    },
    async *observeRange(
      bookmark: StateKeyValues<TState> | null,
      options: ObserveRangeOptions,
    ): AsyncGenerator<BrowseResult<any>> {
      const iterator = rpcClient['state.browse.observe'].call(
        {
          tenant: streamIdentifier.tenant,
          realm: streamIdentifier.realm,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          state: stateName,
          args: streamIdentifier.args,
          bookmark: bookmark ?? undefined,
          take: options?.take,
          direction: options?.dir,
          token: await session.resolveToken(),
          type,
          open: options.open,
        },
        options.signal,
      );
      for await (const item of iterator) {
        yield {
          bookmark: item.bookmark,
          items: item.items.map((i) => ({
            data: mapToState(state, i.data, telemetry),
            keys: i.keys,
          })),
        };
      }
    },
  };
}
